import { useNavigate } from 'react-router';
import * as R from 'Router/routes';
import { isNil } from 'lodash';
import { useCallback, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Button,
  Card,
  Checkbox,
  DocumentModal,
  Placeholder,
  Typography,
} from 'components';
import { useApplicationQuery, useClientOfferQuery } from 'hooks/webapi';
import useAgreementQuery from 'hooks/webapi/client/useAgreementQuery';
import {
  useSecciPDFQuery,
  useSecciQuery,
} from 'hooks/webapi/client/useSecciQuery';
import styles from 'pages/Registration/Agreement/Agreement.module.scss';
import H1 from 'components/H/H1';
import OfferTable from 'components/OfferTable/OfferTable';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

export default function Agreement() {
  const { t } = useTranslation();
  const [legalIsOpen, setLegalIsOpen] = useState(false);
  const [agreementIsOpen, setAgreementIsOpen] = useState(false);
  const [legalChecked, setLegalChecked] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const { data: application } = useApplicationQuery(true, false);
  const { data: agreementHtml } = useAgreementQuery({
    amount: application?.amount,
    term: application?.term,
    fileType: 'HTML',
  });
  const { data: agreementPdf, refetch: refetchAgreementPdf } =
    useAgreementQuery({
      amount: application?.amount,
      enabled: false,
      term: application?.term,
      fileType: 'PDF',
    });

  const { data: secciHtml } = useSecciQuery();
  const { data: secciPdfData, refetch: refetchSecciPdf } = useSecciPDFQuery({
    enabled: false,
  });
  const {
    data: offer,
    isLoading: isLoadingOffer,
    error: errorOffer,
  } = useClientOfferQuery(
    {
      amount: application?.amount,
      term: application?.term,
    },
    !!application,
  );
  const navigate = useNavigate();

  const handleLegalCheck = useCallback(() => {
    if (!legalChecked) setLegalIsOpen(true);
    else setLegalChecked(false);
  }, [legalChecked]);

  const handleOpenAgreementModal = useCallback(() => {
    if (!agreementChecked) setAgreementIsOpen(true);
    else setAgreementChecked(false);
  }, [agreementChecked]);

  const legalOptions = {
    header: {
      title: t('consent:modal_agreement'),
      closeButton: true,
      banner: t('consent:banner'),
    },
    footer: {
      acceptTitle: t('common:confirm'),
      acceptPrint: t('common:print'),
      print: true,
      onAccept: () => setLegalChecked(true),
    },
  };

  const agreementOptions = {
    header: {
      title: t('consent:modal_legal'),
      closeButton: true,
      banner: t('consent:banner'),
    },
    footer: {
      acceptTitle: t('common:confirm'),
      acceptPrint: t('common:print'),
      print: true,
      onAccept: () => setAgreementChecked(true),
    },
  };

  return (
    <>
      <Helmet>
        <title>{t('consent:html_title')}</title>
      </Helmet>
      <div className={styles['box-wrapper']}>
        <H1
          dataTest="agreement-contract-header"
          className={classNames('px-6 lg:px-0', styles.header)}
        >
          {t('consent:contract_documentation')}
        </H1>
      </div>
      <form>
        <Card className={styles.card} dataTest="agreement-card">
          <div className="w-full pb-6">
            <OfferTable
              totalPrincipal={offer?.totalPrincipal}
              totalRepayableAmount={offer?.totalRepayableAmount}
              extensionFees={offer?.extensionFees[30]}
              interestRate={offer?.interestRate}
              assignedInterest={offer?.assignedInterest}
              firstPaymentDate={offer?.firstPaymentDate}
              loading={Boolean(isLoadingOffer || errorOffer)}
              futureExtensionFees={offer?.futureExtensionFees?.[0]?.fee}
            />
          </div>
          <Checkbox
            name="legal"
            className={styles['check-box']}
            onChange={handleLegalCheck}
            disabled={!secciHtml}
            dataTest="agreement-checkbox-legal"
            value={legalChecked}
          >
            <Typography size="s">
              <Placeholder
                loading={!secciHtml}
                dataTest="legal"
                placeholder=""
                replace
              >
                <span
                  className={styles.highlight}
                  dangerouslySetInnerHTML={{
                    __html: t('consent:read'),
                  }}
                />
              </Placeholder>
            </Typography>
          </Checkbox>

          <Checkbox
            name="agreement"
            onChange={handleOpenAgreementModal}
            className={styles['check-box']}
            disabled={!agreementHtml}
            dataTest="agreement-checkbox-agreement"
            value={agreementChecked}
          >
            <Typography size="s">
              <Placeholder
                loading={!agreementHtml}
                dataTest="agreement"
                placeholder=""
                replace
              >
                <span
                  className={styles.highlight}
                  dangerouslySetInnerHTML={{
                    __html: t('consent:comfirm'),
                  }}
                  data-test="agreement-confirm"
                />
              </Placeholder>
            </Typography>
          </Checkbox>
          <Button
            data-test="submit_btn_agreement"
            className="pt-6"
            onClick={() => navigate(R.SIGN_AGREEMENT)}
          >
            {t('common:continue')}
          </Button>
        </Card>
        <div className={styles['box-wrapper']}>
          {legalIsOpen && !!secciHtml && (
            <DocumentModal
              dataTest="legal-modal"
              onClose={() => setLegalIsOpen(false)}
              options={legalOptions}
              className={styles.modal}
              classNameOverlay={styles['modal-overlay']}
              fetchPdf={refetchSecciPdf}
              pdfData={!isNil(secciPdfData) ? secciPdfData : undefined}
            >
              <div
                className={styles['secci-overrides']}
                dangerouslySetInnerHTML={{
                  __html: secciHtml,
                }}
                data-test="agreement-secci"
              />
            </DocumentModal>
          )}
          {agreementIsOpen && !!agreementHtml && (
            <DocumentModal
              dataTest="agreement-modal"
              onClose={() => setAgreementIsOpen(false)}
              options={agreementOptions}
              className={styles.modal}
              classNameOverlay={styles['modal-overlay']}
              fetchPdf={refetchAgreementPdf}
              pdfData={
                !isNil(agreementPdf) && agreementPdf instanceof Blob
                  ? agreementPdf
                  : undefined
              }
            >
              <div
                className={styles['agreement-overrides']}
                dangerouslySetInnerHTML={{
                  __html: agreementHtml as string,
                }}
                data-test="agreement-html"
              />
            </DocumentModal>
          )}
        </div>
      </form>
    </>
  );
}
